import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function EchelleInput({ value, onChange, qid }) {
    return (
        <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    data-testid={`eval-answer-${qid}-${n}`}
                    className={`h-12 flex-1 rounded-xl border text-base font-semibold transition ${
                        value === n
                            ? "border-brand-orange bg-brand-orange text-white"
                            : "border-brand-line bg-white text-brand-ink hover:border-brand-orange"
                    }`}
                >
                    {n}
                </button>
            ))}
        </div>
    );
}

export default function StagiaireEvaluation() {
    const { template_id } = useParams();
    const navigate = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [enrollmentId, setEnrollmentId] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Pull enrollment_id from query param
        const params = new URLSearchParams(window.location.search);
        const eid = params.get("enrollment_id");
        if (!eid) {
            toast.error("Paramètre enrollment_id manquant.");
            navigate("/stagiaire");
            return;
        }
        setEnrollmentId(eid);
        (async () => {
            try {
                const { data } = await api.get(`/stagiaire/evaluations/${template_id}`);
                setTpl(data);
            } catch (err) {
                toast.error(formatApiError(err));
                navigate("/stagiaire");
            } finally {
                setLoading(false);
            }
        })();
    }, [template_id, navigate]);

    const set = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

    const submit = async () => {
        // Validate required
        for (const q of tpl.questions) {
            if (q.required && (answers[q.id] === undefined || answers[q.id] === "" || (Array.isArray(answers[q.id]) && answers[q.id].length === 0))) {
                toast.error(`Question "${q.text.slice(0, 40)}…" obligatoire.`);
                return;
            }
        }
        setSubmitting(true);
        try {
            const payload = {
                answers: Object.entries(answers).map(([qid, value]) => ({
                    question_id: qid,
                    value,
                })),
            };
            const { data } = await api.post(
                `/stagiaire/evaluations/${template_id}/submit?enrollment_id=${enrollmentId}`,
                payload
            );
            toast.success(
                data.score != null
                    ? `Évaluation soumise · score ${data.score}%${data.passed ? " ✓" : ""}`
                    : "Évaluation soumise"
            );
            navigate(`/stagiaire/sessions/${data.session_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !tpl) {
        return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-brand-smoke">Chargement…</div>;
    }

    return (
        <div className="mx-auto max-w-3xl px-6 py-10" data-testid="page-stagiaire-evaluation">
            <Link to="/stagiaire" className="inline-flex items-center gap-2 text-sm text-brand-smoke hover:text-brand-ink">
                <ArrowLeft className="h-4 w-4" /> Retour
            </Link>
            <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                Questionnaire Qualiopi
            </div>
            <h1 className="mt-2 font-display text-4xl font-normal tracking-tightest" data-testid="eval-page-title">
                {tpl.title}
            </h1>
            {tpl.description && (
                <p className="mt-3 text-brand-smoke">{tpl.description}</p>
            )}

            <div className="mt-8 space-y-5">
                {tpl.questions.map((q, idx) => (
                    <div key={q.id} className="rounded-2xl border border-brand-line bg-white p-6" data-testid={`eval-q-${q.id}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                            Question {idx + 1} {q.required && <span className="text-brand-orange">*</span>}
                        </div>
                        <div className="mt-2 text-base font-medium text-brand-ink">{q.text}</div>
                        <div className="mt-4">
                            {q.type === "echelle_1_5" && (
                                <EchelleInput value={answers[q.id]} onChange={(v) => set(q.id, v)} qid={q.id} />
                            )}
                            {q.type === "oui_non" && (
                                <div className="grid grid-cols-2 gap-3">
                                    {["Oui", "Non"].map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => set(q.id, opt.toLowerCase())}
                                            className={`h-12 rounded-xl border text-sm font-semibold transition ${
                                                answers[q.id] === opt.toLowerCase()
                                                    ? "border-brand-orange bg-brand-orange text-white"
                                                    : "border-brand-line bg-white text-brand-ink hover:border-brand-orange"
                                            }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {q.type === "qcm_single" && (
                                <div className="space-y-2">
                                    {(q.options || []).map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => set(q.id, opt)}
                                            className={`block w-full rounded-xl border px-4 py-3 text-left text-sm transition ${
                                                answers[q.id] === opt
                                                    ? "border-brand-orange bg-brand-orange/10 text-brand-ink"
                                                    : "border-brand-line bg-white text-brand-ink hover:border-brand-orange"
                                            }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {q.type === "qcm_multi" && (
                                <div className="space-y-2">
                                    {(q.options || []).map((opt) => {
                                        const list = answers[q.id] || [];
                                        const checked = list.includes(opt);
                                        return (
                                            <label key={opt} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm ${checked ? "border-brand-orange bg-brand-orange/10" : "border-brand-line bg-white"}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        const next = e.target.checked
                                                            ? [...list, opt]
                                                            : list.filter((x) => x !== opt);
                                                        set(q.id, next);
                                                    }}
                                                />
                                                {opt}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                            {q.type === "text_libre" && (
                                <textarea
                                    rows={4}
                                    value={answers[q.id] || ""}
                                    onChange={(e) => set(q.id, e.target.value)}
                                    placeholder="Votre réponse…"
                                    className="w-full resize-none rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange"
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center justify-end">
                <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="eval-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                >
                    <Send className="h-3.5 w-3.5" />
                    {submitting ? "Envoi…" : "Soumettre mes réponses"}
                </button>
            </div>
        </div>
    );
}
